/* eslint-disable */
import {
  sessionManager, Logger, vueExports,
} from 'vue-infra';
import { withFlagProvider } from 'lp-vuelaunchdarkly-mdl';
import { ALL_ROUTES_INVERT, ROUTES } from './const/routes';
import { FEATURE_FLAGS } from './const/featureflags';
import routes from './router/routes';
import { getAvailableApps } from './common/permission';
import { appCode, appName } from '../config';
import { loadI18nResources } from './i18n/resource-resolver';
import './styles/app.scss';
import { PRIVILEGES } from './const/privileges';
import { AppConfig, AppName } from './common/AppConfig';
import { resolveLDClientId } from './common/utils';
import { ACTION_TYPES } from './store/actions';
import { CA_FEATURE_FLAGS, FEATURES } from '@conversationAssist/const/featureflags';
import { CONVERSATION_ASSIST_ROUTES } from '@conversationAssist/const/conversationAssist';
const AppVue = vueExports.defineAsyncComponent(() => import('@/App.vue'));

const appStore = async () => {
  const storeModule = await import('./store');
  return storeModule.default;
};
const logger = Logger.getLogger(appName);

// fixme - add instructions for every prop
const config = {

  id: appName,

  keepDOMRendered: true, // true -> use v-show instead of v-if in the application renderer
  lazy: true,
  autostart: true,

  moduleName: appName,

  routePrefix: appCode,

  // todo - don't just pass any persona
  personaMapping: appName,

  // todo - try passing ['*']
  personas: ['agent', 'agentManager', 'admin', appName],

  switcher: {

    // fixme (LE) - provide fallback in case this is not set
    control: `switcher-item-${appName}`,

    fontIconName: 'ico-automation-platform', // icon for the navigation menu.

    // fixme (LE) - provide fallback in case this is not set
    index: 999,
    titleTranslationKey: 'title',
    tooltipTranslationKey: 'title',
    // todo - try these
    // title: appName,
    // tooltip: appName,
    // iconClass: 'ico-16 ico-switcher-item-automation-platform',
  },

  // @deprecated - will be removed in LE 10.9
  external: true,
  // @deprecated - will be removed in LE 10.9
  regionId: `${appName}-external`,
  // @deprecated - will be removed in LE 10.9
  container: 'work-area-wrapper',
  // @deprecated - will be removed in LE 10.9
  template: 'workAreaItemNoLEClass',
};

// todo - add an example implementation with permission validation from another app
const canInitialize = ({ auth = true } = {}) => {
  if (auth) {
    return true;
  }
  return false;
};

function getAvailableRoutes(ldFlags) {
  const availableRoutes = [];
  const availableApps = getAvailableApps();

  const enabledApps = Object.keys(availableApps).reduce((activeApps, appKey) => {
    const app = availableApps[appKey];
    const appFlag = ldFlags[FEATURE_FLAGS[app.code]];
    // eslint-disable-next-line no-prototype-builtins
    const hasLdFeature = FEATURE_FLAGS.hasOwnProperty(app.code);
    if (appFlag === true || !hasLdFeature) {
      // eslint-disable-next-line no-param-reassign
      activeApps[appKey] = app;
    }
    return activeApps;
  }, {});

  routes.forEach((route) => {
    const routeAppCode = ALL_ROUTES_INVERT[route.name];

    const isRecommendationSourcesDisabled = ldFlags[CA_FEATURE_FLAGS[FEATURES.RECOMMENDATION_SOURCES_READ_ONLY]];

    if (route.name === ROUTES.CONVERSATION_ASSIST) {
        route.children = route.children
          .filter(childRoute => {
            if (childRoute.name === CONVERSATION_ASSIST_ROUTES.RECOMMENDATION_RULES) {
              return isRecommendationSourcesDisabled;
            }

            return true;
          });
    }

    if (enabledApps[routeAppCode]) {
      availableRoutes.push(route);
    }
  });

  return availableRoutes;
}

async function getLDFlags() {
  const LDFlagsMixin = withFlagProvider({
    clientSideId: resolveLDClientId(sessionManager.getAccountZone()),
    user: { key: sessionManager.accountId },
  });
  await LDFlagsMixin.methods.initLD();

  return LDFlagsMixin.methods.flags;
}

const applicationInit = async (leAppConfig) => {
  // AC Features & permissions check go here
  if (!canInitialize(leAppConfig) || !sessionManager.hasAutomationAreaFeature() || !sessionManager.hasPrivilege(PRIVILEGES.AUTOMATION_AREA)) {
    return {};
  }


  const i18nResources = await loadI18nResources(leAppConfig.locale, leAppConfig.fallbackLocale);
  const leConfig = window.LE && window.LE.getConfig();
  // eslint-disable-next-line no-underscore-dangle
  const isNewNavbar = Boolean(leConfig.__isNewNavbar);
  const ldFlags = await getLDFlags();
  const moduleRoutes = isNewNavbar ? getAvailableRoutes(ldFlags) : routes;

  AppConfig.setConfig(leAppConfig.config);

  const store = await appStore();
  store.dispatch(`${AppName}/${ACTION_TYPES.SET_LD_FLAGS}`, (ldFlags) || {});
  store.dispatch(`${AppName}/${ACTION_TYPES.SET_LOCALE}`, leAppConfig.locale);

  return {
    vueComponent: {
      name: appCode,
      setup: () => () => vueExports.h(AppVue),
    },
    appId: appCode,
    routes: moduleRoutes,
    ...i18nResources,
  };
};

const webpackPathInit = (leAppConfig) => {
  __webpack_public_path__ = leAppConfig.publicPath; // eslint-disable-line
};

// eslint-disable-next-line func-names
const getBackboneModuleDictionary = async function (locale, fallbackLocale) {
  const localDictionary = {};
  // load custom local
  try {
    localDictionary.localResource = await loadInContext(locale);
  } catch (e) {
    logger.error(`fail to load dictionary ${locale}`);
  }
  // load fallback local
  if (locale !== fallbackLocale) {
    try {
      localDictionary.fallbackLocaleResource = await loadInContext(fallbackLocale);
    } catch (e) {
      logger.error(`fail to load dictionary ${fallbackLocale}`);
    }
  }
  return localDictionary;
};

async function loadInContext(filename) {
  return new Promise((resolve) => {
    if (filename === 'en-US') {
      // en-US is excluded from bundle-loader and pre-loaded by default
      require([`./i18n/dictionaries/${filename}.json`], resolve);
    } else {
      // all other locals are loaded dynamically using the bundle-loader
      require(`./i18n/dictionaries/${filename}.json`)((dict) => {
        resolve(dict);
      });
    }
  });
}

export default {
  keepDOMRendered: true,
  config,
  // todo - rename to 'initialize'
  applicationInit,
  // todo - rename as lifecycle hook: 'on[Something]', i.e. according to the invoked LE lifecycle event.
  // todo - suggestions: onBootstrap, onAppsLoad
  webpackPathInit,
  getBackboneModuleDictionary,
};
